import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, CheckCircle, Warning, Calendar, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import { billingApi } from "@/lib/api";

const STATUS_META = {
  trial: { label: "Free Trial", color: "#4682b4", note: "Enjoying the free 14-day evaluation. Choose a plan any time to lock in." },
  active: { label: "Active", color: "#22c55e", note: "Your subscription is current." },
  trial_expired: { label: "Trial Expired", color: "#ef4444", note: "Your free trial has ended. Pick a plan to keep dispatching." },
  cancelled: { label: "Cancelled", color: "#71717a", note: "Subscription cancelled. Re-activate at any time." },
};

export default function BillingPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billingApi.status()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="h-[calc(100vh-72px)] overflow-auto" data-testid="billing-page">
      <div className="max-w-4xl mx-auto p-4 lg:p-8 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] flex items-center gap-2">
            <CreditCard size={11} weight="fill" className="text-[#ff8c00]" /> Billing & Subscription
          </div>
          <h1 className="font-heading font-black text-2xl tracking-tight">Plan & payments</h1>
        </div>

        {loading && (
          <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-8 text-center text-xs text-[#71717a] uppercase tracking-[0.2em] flex items-center justify-center gap-2">
            <ArrowsClockwise size={12} className="animate-spin" /> Loading…
          </div>
        )}

        {status && <StatusCard status={status} />}

        <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
          <div className="border-b border-[#2a2a2a] px-4 py-3 flex items-center justify-between">
            <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Manage subscription</h2>
            <Link
              to="/pricing"
              className="text-[10px] uppercase tracking-[0.2em] text-[#ff8c00] hover:underline font-bold flex items-center gap-1"
              data-testid="billing-change-plan"
            >
              Change plan <ArrowRight size={10} weight="bold" />
            </Link>
          </div>
          <div className="p-5 text-xs text-[#a1a1aa] leading-relaxed">
            All plans are billed in USD via Stripe-secured checkout. Each payment grants 30 days of access. You can switch plans at any time — switching mid-cycle adds 30 days on top of whatever you already have.
            <br /><br />
            Need a custom plan, ACH/wire, or invoiced billing? Reply to your welcome email and we'll set it up by hand.
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ status }) {
  const meta = STATUS_META[status.subscription_status] || STATUS_META.trial;
  const isWarning = status.subscription_status === "trial_expired";
  return (
    <div className="bg-[#0d0d0d] border-l-4 border" style={{ borderLeftColor: meta.color, borderColor: "#2a2a2a" }}>
      <div className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-3">
            {isWarning ? (
              <Warning size={22} weight="fill" style={{ color: meta.color }} />
            ) : (
              <CheckCircle size={22} weight="fill" style={{ color: meta.color }} />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: meta.color }}>{meta.label}</div>
              <div className="font-heading font-black text-lg tracking-tight">
                {status.subscription_plan ? `${status.subscription_plan.toUpperCase()} Plan` : "No active plan"}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono font-bold text-2xl">
              {status.days_remaining}
              <span className="text-xs text-[#71717a] ml-1">days</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">remaining</div>
          </div>
        </div>
        <p className="text-xs text-[#a1a1aa]">{meta.note}</p>
        {(status.trial_ends_at || status.subscription_renews_at) && (
          <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#71717a]">
            <Calendar size={10} />
            {status.subscription_renews_at
              ? <>Renews / expires <strong className="text-white">{new Date(status.subscription_renews_at).toLocaleDateString()}</strong></>
              : <>Trial ends <strong className="text-white">{new Date(status.trial_ends_at).toLocaleDateString()}</strong></>}
          </div>
        )}
      </div>
    </div>
  );
}
